import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { CalendarClock, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

const fmtDay = (iso) => new Date(iso).toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "long" });
const fmtTime = (iso) => new Date(iso).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });

export default function Termin() {
  const nav = useNavigate();
  const [slots, setSlots] = useState([]);
  const [mine, setMine] = useState([]);
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, m] = await Promise.all([api.get("/appointments/slots"), api.get("/appointments")]);
      setSlots(s.data); setMine(m.data);
    } catch (e) { /* ignore */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const book = async () => {
    if (!selected) return;
    setBooking(true);
    try {
      await api.post("/appointments", { slot: selected, note });
      toast.success("Termin gebucht! Bestätigung folgt per E-Mail.");
      setSelected(null); setNote(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBooking(false);
  };

  const byDay = slots.reduce((acc, s) => { const k = s.slice(0, 10); (acc[k] = acc[k] || []).push(s); return acc; }, {});

  return (
    <div className="min-h-screen bg-muted/30">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex items-center gap-3 mb-2">
          <span className="grid place-items-center h-11 w-11 rounded-md bg-[#0A192F] text-emerald-400"><CalendarClock size={22} /></span>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold">Beratungstermin buchen</h1>
            <p className="text-muted-foreground text-sm">Optionales, kostenloses Erstgespräch zu Ihrem Gründungsvorhaben.</p>
          </div>
        </div>

        {mine.length > 0 && (
          <Card className="p-5 border-emerald-200 bg-emerald-50 mb-6">
            <h3 className="font-semibold text-emerald-900 mb-2">Ihre gebuchten Termine</h3>
            {mine.map((a) => (
              <div key={a.id} data-testid={`my-appt-${a.id}`} className="flex items-center gap-2 text-sm text-emerald-800">
                <Check size={15} /> {fmtDay(a.slot)}, {fmtTime(a.slot)} Uhr
              </div>
            ))}
          </Card>
        )}

        {loading ? (
          <div className="grid place-items-center py-20"><Loader2 className="animate-spin text-emerald-500" /></div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-5">
              {Object.keys(byDay).length === 0 && <Card className="p-8 text-center text-muted-foreground">Aktuell sind keine freien Termine verfügbar.</Card>}
              {Object.entries(byDay).map(([day, times]) => (
                <Card key={day} className="p-5 border-border">
                  <h3 className="font-semibold capitalize mb-3">{fmtDay(times[0])}</h3>
                  <div className="flex flex-wrap gap-2">
                    {times.map((t) => (
                      <button key={t} data-testid={`slot-${t}`} onClick={() => setSelected(t)}
                        className={`px-4 py-2 rounded-md text-sm font-medium border transition-colors ${selected === t ? "bg-emerald-500 text-white border-emerald-500" : "border-border hover:border-[#0A192F]"}`}>
                        {fmtTime(t)}
                      </button>
                    ))}
                  </div>
                </Card>
              ))}
            </div>
            <div>
              <Card className="p-5 border-border sticky top-20">
                <h3 className="font-semibold mb-3">Ihre Buchung</h3>
                {selected ? (
                  <Badge className="bg-[#0A192F] text-white mb-3">{fmtDay(selected)}, {fmtTime(selected)} Uhr</Badge>
                ) : <p className="text-sm text-muted-foreground mb-3">Bitte wählen Sie einen freien Zeitpunkt.</p>}
                <Textarea data-testid="appt-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Anliegen / Notiz (optional)" rows={3} />
                <Button data-testid="appt-book" onClick={book} disabled={!selected || booking} className="w-full mt-3 bg-emerald-500 hover:bg-emerald-600 text-white">
                  {booking ? <Loader2 size={16} className="animate-spin mr-1" /> : null} Termin buchen
                </Button>
                <button onClick={() => nav("/portal")} className="w-full text-center text-sm text-muted-foreground hover:text-foreground mt-3">Zurück zum Portal</button>
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
